/**
 * Automations — Screen 7
 * Read-only sequence list; editor greyed.
 */

import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { fetchSequences } from '../../lib/apiHelpers.js';
import { ComingSoon } from '../../components/ComingSoon.js';
import type { SequenceListItem } from '../../lib/apiHelpers.js';
import { ManagedPill, ViewOnlyRibbon } from '../../components/ManagedChrome.js';

const GOAL_CONFIG = {
  transfer: { label: 'Live Transfer', color: '#6D4AEA', bg: 'rgba(109,74,234,0.1)' },
  book: { label: 'Book Appointments', color: '#D97706', bg: 'rgba(217,119,6,0.1)' },
  close: { label: 'Close Sales', color: '#159662', bg: 'rgba(21,150,98,0.1)' },
};

// Demo data (mirrors seed)
const DEMO_SEQUENCES: SequenceListItem[] = [
  {
    id: 's1',
    name: 'Fresh Lead — Speed to Contact',
    goal: 'transfer',
    active: true,
    trigger: 'Lead received via webhook or Facebook Lead Ads',
    enrolled: 47,
    connectRate: 31,
    bookedRate: 68,
    exitDays: 9,
    stepCount: 9,
  },
  {
    id: 's2',
    name: 'Aged Lead Revival',
    goal: 'book',
    active: true,
    trigger: 'Lead age > 30 days, no contact',
    enrolled: 18,
    connectRate: 22,
    bookedRate: 44,
    exitDays: 6,
    stepCount: 6,
  },
  {
    id: 's3',
    name: 'No-Show Recovery',
    goal: 'book',
    active: true,
    trigger: 'Appointment status = no_show',
    enrolled: 6,
    connectRate: 50,
    bookedRate: 33,
    exitDays: 4,
    stepCount: 4,
  },
  {
    id: 's4',
    name: 'Post-Sale Referral Ask',
    goal: 'close',
    active: false,
    trigger: 'Stage = Closed, 7 days after close',
    enrolled: 12,
    connectRate: 0,
    bookedRate: 0,
    exitDays: 3,
    stepCount: 3,
  },
];

function SequenceCard({ seq }: { seq: SequenceListItem }) {
  const goal = GOAL_CONFIG[seq.goal];

  return (
    <div
      className="bg-surface rounded-card shadow-card p-4 flex items-center gap-4"
      style={{ opacity: seq.active ? 1 : 0.65 }}
    >
      {/* Main info */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1">
          <p
            className="text-[14px] font-semibold text-ink truncate"
            style={{ fontFamily: '"Plus Jakarta Sans", system-ui' }}
          >
            {seq.name}
          </p>
          {!seq.active && (
            <span
              className="px-2 py-0.5 rounded-chip text-[10px] font-medium flex-shrink-0"
              style={{ background: '#F2F4F7', color: '#64748B', border: '1px solid #E5EAF2' }}
            >
              Off
            </span>
          )}
        </div>

        <div className="flex items-center gap-2 mb-2">
          <span
            className="px-2 py-0.5 rounded-chip text-[10px] font-semibold flex-shrink-0"
            style={{ background: goal.bg, color: goal.color }}
          >
            {goal.label}
          </span>
        </div>

        <p className="text-[11.5px] text-muted truncate">{seq.trigger}</p>
      </div>

      {/* Stats */}
      <div className="flex items-center gap-6 flex-shrink-0">
        <div className="text-center">
          <p
            className="text-[13.5px] font-medium text-ink"
            style={{ fontFamily: '"DM Mono", monospace' }}
          >
            {seq.stepCount} steps · {seq.exitDays}d
          </p>
          <p className="text-[10px] text-muted">length</p>
        </div>
        <div className="text-center">
          <p
            className="text-[13.5px] font-medium text-ink"
            style={{ fontFamily: '"DM Mono", monospace' }}
          >
            {seq.enrolled}
          </p>
          <p className="text-[10px] text-muted">enrolled</p>
        </div>
        <div className="text-center">
          <p
            className="text-[13.5px] font-medium text-ink"
            style={{ fontFamily: '"DM Mono", monospace' }}
          >
            {seq.connectRate}%
          </p>
          <p className="text-[10px] text-muted">connect</p>
        </div>
        <div className="text-center">
          <p
            className="text-[13.5px] font-medium text-ink"
            style={{ fontFamily: '"DM Mono", monospace' }}
          >
            {seq.bookedRate}%
          </p>
          <p className="text-[10px] text-muted">booked</p>
        </div>
      </div>

      {/* Toggle + chevron — greyed */}
      <div className="flex items-center gap-3 flex-shrink-0">
        <ComingSoon inline label="Editor coming soon">
          <div />
        </ComingSoon>
        <div className="opacity-40">
          <div
            className="relative rounded-toggle cursor-not-allowed"
            style={{
              width: 36,
              height: 20,
              background: seq.active ? '#159662' : '#D8DFEA',
            }}
          >
            <span
              className="absolute top-[2px] rounded-full bg-white"
              style={{
                width: 16,
                height: 16,
                left: seq.active ? 18 : 2,
                transition: 'left 0.2s',
              }}
            />
          </div>
        </div>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-muted opacity-40">
          <polyline points="9 18 15 12 9 6" />
        </svg>
      </div>
    </div>
  );
}

export function AutomationsPage() {
  const { data } = useQuery({
    queryKey: ['sequences'],
    queryFn: fetchSequences,
  });

  const sequences = data ?? DEMO_SEQUENCES;

  return (
    <div>
      {/* Header */}
      <ViewOnlyRibbon
        title="Sequences are managed by ContactRate"
        detail="Full theater — see what’s running. Cadence, attempts, and iMessage/voice steps stay with us."
      />
      <div className="flex items-start justify-between mb-5">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <h1
              className="text-[22px] font-bold text-ink tracking-[-0.01em]"
              style={{ fontFamily: '"Plus Jakarta Sans", system-ui' }}
            >
              Automations
            </h1>
            <ManagedPill />
          </div>
          <p className="text-[13.5px] text-muted mt-0.5">
            <span style={{ fontFamily: '"DM Mono", monospace' }}>{sequences.length}</span> sequences · Ava runs every step automatically
          </p>
        </div>
        <ComingSoon inline label="Editor managed by ContactRate">
          <button
            className="px-3 py-1.5 rounded-btn text-[11.5px] font-semibold text-white"
            style={{ background: '#2E66E0' }}
          >
            + New automation
          </button>
        </ComingSoon>
      </div>

      {/* Sequence list */}
      <div className="flex flex-col gap-3">
        {sequences.map((seq) => (
          <SequenceCard key={seq.id} seq={seq} />
        ))}
      </div>

      {/* Editor section — greyed */}
      <div className="mt-8">
        <ComingSoon label="Sequence editor — coming soon">
          <div className="bg-surface rounded-card shadow-card p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-8 h-8 rounded-full bg-surface-alt" />
              <div>
                <div className="h-4 w-48 bg-surface-alt rounded mb-1.5" />
                <div className="h-3 w-32 bg-hairline rounded" />
              </div>
            </div>
            <div className="flex flex-col gap-2">
              {[1, 2, 3].map((n) => (
                <div key={n} className="flex items-center gap-3 p-3 bg-surface-alt rounded-[10px]">
                  <div className="w-6 h-6 rounded-full bg-hairline" />
                  <div className="flex-1 h-3 bg-hairline rounded" />
                </div>
              ))}
            </div>
          </div>
        </ComingSoon>
      </div>
    </div>
  );
}
